import React, { useState, useEffect, useMemo } from 'react';
import { fetchStandards, fetchDomains } from '../../api/standards';
import styles from './StandardsPicker.module.css';

// Map wizard subjects to standards API subjects
const SUBJECT_MAP = {
  'Math': 'Mathematics',
  'ELA': 'English Language Arts',
  'Science': 'Science',
  'Social Studies': 'Social Studies',
  'Other': '',
};

export default function StandardsPicker({ subject, gradeLevel, selectedStandards, onAdd, onRemove }) {
  const [allStandards, setAllStandards] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch standards and domains when subject/grade change
  useEffect(() => {
    if (!subject || !gradeLevel) return;
    setLoading(true);
    setSelectedDomain('');
    setSearchText('');

    const mappedSubject = SUBJECT_MAP[subject] || subject;
    const params = { grade: gradeLevel };
    if (mappedSubject) params.subject = mappedSubject;

    Promise.all([
      fetchStandards(params),
      fetchDomains(gradeLevel, mappedSubject || undefined),
    ])
      .then(([stdRes, domRes]) => {
        setAllStandards(Array.isArray(stdRes.data) ? stdRes.data : []);
        setDomains(Array.isArray(domRes.data) ? domRes.data : []);
      })
      .catch((err) => {
        console.error('Failed to load standards:', err);
        setAllStandards([]);
        setDomains([]);
      })
      .finally(() => setLoading(false));
  }, [subject, gradeLevel]);

  const selectedIds = useMemo(() => new Set(selectedStandards.map(s => s.id)), [selectedStandards]);

  // Client-side filtering by domain and search text
  const filtered = useMemo(() => {
    let list = allStandards;
    if (selectedDomain) {
      list = list.filter(s => s.domainCode === selectedDomain);
    }
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(s =>
        s.standardCode.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allStandards, selectedDomain, searchText]);

  // Group filtered results by domain
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(s => {
      if (!map[s.domainCode]) {
        map[s.domainCode] = { domainCode: s.domainCode, domainName: s.domainName, standards: [] };
      }
      map[s.domainCode].standards.push(s);
    });
    return Object.values(map);
  }, [filtered]);

  if (!subject || !gradeLevel) return null;

  return (
    <div className={styles.picker}>
      {/* Selected standards chips */}
      {selectedStandards.length > 0 && (
        <div className={styles.chips}>
          {selectedStandards.map(std => (
            <div key={std.id} className={styles.chip}>
              <span className={styles.chipCode}>{std.standardCode}</span>
              <button
                className={styles.chipRemove}
                onClick={() => onRemove(std)}
                title="Remove"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Filters row */}
      <div className={styles.filters}>
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          className={styles.domainSelect}
          aria-label="Filter by domain"
        >
          <option value="">All Domains</option>
          {domains.map(d => (
            <option key={d.domainCode} value={d.domainCode}>
              {d.domainCode} — {d.domainName}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search standards..."
          className={styles.searchInput}
          aria-label="Search standards"
        />
      </div>

      {/* Results */}
      <div className={styles.results}>
        {loading && <p className={styles.statusText}>Loading standards...</p>}
        {!loading && filtered.length === 0 && (
          <p className={styles.statusText}>No standards found.</p>
        )}
        {!loading && grouped.map(group => (
          <div key={group.domainCode} className={styles.domainGroup}>
            <div className={styles.domainHeader}>
              {group.domainCode} — {group.domainName}
            </div>
            {group.standards.map(std => {
              const isSelected = selectedIds.has(std.id);
              return (
                <div
                  key={std.id}
                  className={`${styles.resultItem} ${isSelected ? styles.resultSelected : ''}`}
                  onClick={() => !isSelected && onAdd(std)}
                >
                  <span className={styles.resultCode}>{std.standardCode}</span>
                  <span className={styles.resultDesc}>{std.description}</span>
                  {isSelected && <span className={styles.addedLabel}>Added</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
